// Import of the module
import { PencilIcon } from "@heroicons/react/24/solid";
import { DownloadButton } from "@/components/DownloadButton";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { Button } from "@heroui/react";
import { ColumnDef } from "@tanstack/react-table";

import { getCurrencyCode } from "./config";

const client = "etl-quantilia";
type TFunc = (key: string, options?: Record<string, unknown>) => string;

// Type of the fields in the table
export type ForeignExchangeRate = {
  id: number;
  code: string;
  name: string;
  description: string;
  currency_from: number;
  currency_to: number;
  start_date: string | null;
  end_date: string | null;
  bank_holidays: string[];
  created_at: string;
  updated_at: string;
};

// Definition of the columns
export const getForeignExchangeRateColumns = (
  t: TFunc,
): ColumnDef<ForeignExchangeRate>[] => [
  {
    accessorKey: "id",
    header: t("foreign_exchange_rate.columns.id"),
  },
  {
    accessorKey: "code",
    header: t("foreign_exchange_rate.columns.code"),
    cell: ({ row }) => {
      const code = row.getValue<string>("code");

      return (
        <div className="flex items-center">
          <span className="inline-flex items-center rounded-md border border-default-200 bg-default-50 px-2 py-1 font-mono text-xs font-semibold text-default-700">
            {code}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "name",
    header: t("foreign_exchange_rate.columns.name"),
    cell: ({ row }) => {
      const value = row.getValue<string>("name");
      return (
        <div className="max-w-[28rem] truncate text-sm text-default-700">
          {value}
        </div>
      );
    },
  },
  {
    accessorKey: "currency_from",
    header: t("foreign_exchange_rate.columns.currency_from"),
    cell: ({ row }) => {
      const value =
        row.getValue<ForeignExchangeRate["currency_from"]>("currency_from");
      return getCurrencyCode(value);
    },
  },
  {
    accessorKey: "currency_to",
    header: t("foreign_exchange_rate.columns.currency_to"),
    cell: ({ row }) => {
      const value =
        row.getValue<ForeignExchangeRate["currency_to"]>("currency_to");
      return getCurrencyCode(value);
    },
  },
  {
    accessorKey: "start_date",
    header: t("foreign_exchange_rate.columns.start_date"),
    cell: ({ row }) => {
      const value = row.getValue<string | null>("start_date");
      return value ? parseDate(value) : "-";
    },
  },
  {
    accessorKey: "end_date",
    header: t("foreign_exchange_rate.columns.end_date"),
    cell: ({ row }) => {
      const value = row.getValue<string | null>("end_date");
      return value ? parseDate(value) : "-";
    },
  },
  {
    id: "actions",
    header: () => (
      <div className="text-right">
        {t("foreign_exchange_rate.columns.actions")}
      </div>
    ),
    cell: ({ row }) => {
      const id = row.getValue<number>("id");
      const code = row.getValue<string>("code");
      const href = `${
        import.meta.env.VITE_BACKEND_URL
      }/${client}/foreign_exchange_rates/${id}/download-daily-rates-excel`;
      const filename = `foreign_exchange_rates_${code}_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.xlsx`;

      return (
        <div className="flex justify-end">
          <div className="flex items-center gap-1 rounded-lg border border-default-200 bg-default-50/60 p-1">
            <DownloadButton
              href={href}
              filename={filename}
              isIconOnly
              size="sm"
              variant="light"
              color="default"
              textKey="foreign_exchange_rate.actions.download_daily_rates_excel"
            />
            <div className="h-5 w-px bg-default-200" aria-hidden="true" />
            <Link href={`${window.location.pathname}/${id}`}>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="default"
                aria-label={t("foreign_exchange_rate.actions.edit_aria", {
                  code,
                })}
              >
                <PencilIcon className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      );
    },
  },
];
